import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, MessageCircle, Phone } from "lucide-react";
import { NAV_LINKS, WHATSAPP_LINK, CALL_LINK } from "../data/content";

export const openEnquiry = () => window.dispatchEvent(new Event("jamoon:open-enquiry"));

const linkCls = ({ isActive }) =>
  `whitespace-nowrap text-[11px] font-medium tracking-[0.18em] transition-colors duration-300 ${
    isActive ? "text-brass" : "text-ivory/70 hover:text-brass"
  }`;

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 backdrop-blur-xl transition-colors duration-500 ${
          scrolled
            ? "border-b border-brass/15 bg-charcoal/85"
            : "border-b border-transparent bg-charcoal/40"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
          <Link to="/" data-testid="nav-home-logo" aria-label="The Jamoon — home" className="flex items-center">
            <img
              src="/logo-wordmark.png"
              alt="The Jamoon, Corbett"
              className="h-8 w-auto"
            />
          </Link>

          <nav className="hidden items-center gap-6 xl:flex" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`} className={linkCls}>
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden items-center gap-3 xl:flex">
            <a
              data-testid="nav-call-btn"
              href={CALL_LINK}
              aria-label="Call The Jamoon"
              className="flex items-center justify-center border border-brass/40 p-2 text-brass transition-colors duration-300 hover:border-brass hover:bg-brass/10"
            >
              <Phone className="h-4 w-4" />
            </a>
            <a
              data-testid="nav-whatsapp-btn"
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp"
              className="flex items-center justify-center border border-brass/40 p-2 text-brass transition-colors duration-300 hover:border-brass hover:bg-brass/10"
            >
              <MessageCircle className="h-4 w-4" />
            </a>
            <Link
              data-testid="nav-plan-stay-btn"
              to="/plan"
              className="whitespace-nowrap bg-brass px-5 py-2 text-[11px] font-semibold tracking-[0.2em] text-charcoal transition-colors duration-300 hover:bg-[#B89B6A]"
            >
              PLAN YOUR STAY
            </Link>
          </div>

          <button
            data-testid="mobile-menu-open-btn"
            onClick={() => setOpen(true)}
            className="text-ivory xl:hidden"
            aria-label="Open menu"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-nav-drawer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] flex flex-col bg-forest"
          >
            <div className="flex items-center justify-between px-5 py-4">
              <img
                src="/logo-full.png"
                alt=""
                className="h-14 w-auto"
              />
              <button
                data-testid="mobile-menu-close-btn"
                onClick={() => setOpen(false)}
                className="text-ivory"
                aria-label="Close menu"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8" aria-label="Mobile">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i + 0.1, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                  <NavLink
                    data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className="block border-b border-ivory/10 py-4 font-serif text-3xl text-ivory transition-colors duration-300 hover:text-brass"
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.5 }}
              className="flex gap-3 px-8 pb-10"
            >
              <a
                data-testid="mobile-drawer-whatsapp-btn"
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-1 items-center justify-center gap-2 border border-brass/40 py-3 text-xs tracking-[0.2em] text-brass"
              >
                <MessageCircle className="h-4 w-4" /> WHATSAPP
              </a>
              <Link
                data-testid="mobile-drawer-plan-stay-btn"
                to="/plan"
                onClick={() => setOpen(false)}
                className="flex-1 bg-brass py-3 text-center text-xs font-semibold tracking-[0.2em] text-charcoal"
              >
                PLAN YOUR STAY
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
